import React, { useRef } from "react";
import { Z } from "../../../theme/zIndex";
import { useMediaQuery } from "@mui/material";
import {
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Alert,
  Tooltip,
} from "@mui/material";
import { Reservation } from "../types/Reservation";
import { useEditReservation } from "../hooks/useEditReservation";
import EditReservationForm from "./EditReservationForm";
import DepartmentChangeModal from "./DepartmentChangeModal";
import { validateFormForSubmit } from "./ReservationValidator";
import { logger } from "../../../utils/logger";
import { AxiosError } from "axios";
import AppDialog from "../../shared/components/AppDialog";
import { parseMonetaryValue } from "../../shared/utils/formatters";
import { formatDateKey } from "../../shared/utils/dateUtils";

interface EditReservationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (reservation: Reservation) => void;
  selectedReservation: Reservation | null;
  existingReservations: Reservation[];
}

const EditReservationModalRefactored: React.FC<EditReservationModalProps> = ({
  open,
  onClose,
  onSuccess,
  selectedReservation,
  existingReservations,
}) => {
  const isShortDesktop = useMediaQuery("(min-width:900px) and (max-height:820px)");
  const isMobile = useMediaQuery("(max-width:599.95px)");
  const submitInFlightRef = useRef(false);

  const {
    // Estados
    activeTab,
    formData,
    setFormData,
    loading,
    setLoading,
    error,
    setError,
    errors,
    setErrors,
    zonas,
    errorZonas,
    overlapError,
    showPriceConfirmModal,
    pendingDepartmentChange,
    esPagado,
    saldoPendiente,

    // Funciones
    handleDateChange,
    handleTextChange,
    handleMarcarPagado,
    handleMantenerPrecioEspecial,
    handleUsarPrecioBase,
    handleCancelarCambioDepartamento,
    handleTabChange,

    // Mutaciones
    updateReservationMutation,
  } = useEditReservation(selectedReservation, existingReservations);

  const hasGuestInfo =
    !!formData.afiliado_nombre?.trim() ||
    !!formData.afiliado_apellido?.trim() ||
    !!formData.afiliado_dni?.trim();
  const requiresGuestInfo =
    selectedReservation?.huesped_info_status === "faltante" ||
    (!!selectedReservation?.cliente?.es_empresa &&
      selectedReservation.cliente.origen_reserva !== "booking" &&
      selectedReservation.cliente.origen_reserva !== "airbnb");
  const guestInfoMissing = requiresGuestInfo && !hasGuestInfo;
  const huespedesTabLabel = (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
    >
      {isMobile ? "Huésped" : "Huéspedes"}
      {guestInfoMissing && (
        <Tooltip title="Falta huésped o afiliado" arrow>
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "error.main",
              display: "inline-block",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;

    logger.debug("Guardando edición de reserva:", selectedReservation?.id);

    if (
      !validateFormForSubmit(
        formData,
        setErrors,
        overlapError,
        selectedReservation?.cliente?.origen_reserva,
        !!selectedReservation?.cliente?.es_empresa
      )
    ) {
      return;
    }

    submitInFlightRef.current = true;
    setLoading(true);

    try {
      if (overlapError) {
        setErrors({
          entrada: overlapError,
          salida: overlapError,
        });
        setLoading(false);
        return;
      }

      const reservaData = {
        entrada: formData.entrada ? formatDateKey(formData.entrada) : undefined,
        salida: formData.salida ? formatDateKey(formData.salida) : undefined,
        hora_inicio: "14:00",
        hora_fin: "10:00",
        departamento: formData.departamento,
        nombre: formData.nombre,
        apellido: formData.apellido,
        dni: formData.dni,
        pago: formData.pago,
        tipo_ajuste: formData.tipo_ajuste,
        cantidad_huespedes: Number(formData.cantidad_huespedes),
        tipo_cama: formData.tipo_cama,
        localidad: formData.localidad || "",
        email1: formData.email1 || "",
        email2: formData.email2 || "",
        telefono1: formData.telefono1 || "",
        telefono2: formData.telefono2 || "",
        acompanantes: Array.isArray(formData.acompanantes)
          ? formData.acompanantes
          : [],
        afiliado_nombre: formData.afiliado_nombre || "",
        afiliado_apellido: formData.afiliado_apellido || "",
        afiliado_dni: formData.afiliado_dni || "",
        late_checkout: formData.late_checkout,
      };

      const origenCliente = selectedReservation?.cliente?.origen_reserva;
      const esReservaExterna =
        origenCliente === "booking" || origenCliente === "airbnb";
      const dniTrim = (formData.dni || "").trim();

      const updatedReservation = await updateReservationMutation.mutateAsync({
        id: selectedReservation?.id || 0,
        entrada: reservaData.entrada,
        salida: reservaData.salida,
        departamento: reservaData.departamento,
        nombre: reservaData.nombre,
        apellido: reservaData.apellido,
        dni: reservaData.dni,
        origen_reserva: origenCliente || "directo",
        codigo_reserva_externa: esReservaExterna ? dniTrim : undefined,
        codigo_booking: origenCliente === "booking" ? dniTrim : null,
        pago: Number(reservaData.pago),
        valor_total: parseMonetaryValue(formData.valor_total),
        tipo_ajuste: reservaData.tipo_ajuste,
        cantidad_huespedes: reservaData.cantidad_huespedes,
        tipo_cama: reservaData.tipo_cama,
        localidad: reservaData.localidad,
        email1: reservaData.email1,
        email2: reservaData.email2,
        telefono1: reservaData.telefono1,
        telefono2: reservaData.telefono2,
        acompanantes: reservaData.acompanantes,
        afiliado_nombre: reservaData.afiliado_nombre,
        afiliado_apellido: reservaData.afiliado_apellido,
        afiliado_dni: reservaData.afiliado_dni,
        late_checkout: reservaData.late_checkout,
        sin_limpieza_automatica: formData.sin_limpieza_automatica,
        hora_inicio: reservaData.hora_inicio,
        hora_fin: reservaData.hora_fin,
        politica_precio: formData.politica_precio || undefined,
        precio_personalizado: formData.precio_personalizado || undefined,
        tipo_precio_personalizado:
          formData.tipo_precio_personalizado || undefined,
        dias_limpieza_pactados:
          formData.usar_limpiezas_manuales &&
          formData.dias_limpieza_pactados.length > 0
            ? formData.dias_limpieza_pactados
            : null,
        dias_limpieza_recambio:
          formData.usar_limpiezas_manuales &&
          formData.dias_limpieza_recambio.length > 0
            ? formData.dias_limpieza_recambio
            : null,
      });

      logger.debug("Reserva actualizada exitosamente:", updatedReservation);
      onSuccess(updatedReservation);
      onClose();
    } catch (error) {
      logger.error("Error al guardar la reserva:", error);
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.error || "Error al actualizar la reserva";
        setError(errorMessage);
        logger.error("Error al actualizar la reserva:", error.response?.data);
      } else {
        setError("Error al actualizar la reserva");
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const shortDesktopWidth = "min(1080px, calc(100vw - 32px))";

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        maxWidth={isShortDesktop ? false : "sm"}
        fullWidth
        scroll="paper"
        sx={{ zIndex: Z.modal }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "calc(100dvh - 24px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            ...(isShortDesktop
              ? {
                  margin: "12px auto",
                  maxWidth: shortDesktopWidth,
                  width: shortDesktopWidth,
                }
              : {
                  margin: 0,
                  top: 0,
                }),
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            background: "linear-gradient(135deg, #3a8fd8 0%, #2ec6c9 100%)",
            color: "white",
            py: isMobile || isShortDesktop ? 0.75 : 1,
            px: isMobile ? 1.5 : 2,
            m: 0,
            borderRadius: "12px 12px 0 0",
            borderTop: 0,
            ...(isShortDesktop && { fontSize: "1.05rem" }),
          }}
        >
          Editar Reserva
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: isMobile ? 1.25 : isShortDesktop ? 1.5 : 2,
            py: isMobile ? 1 : isShortDesktop ? 1.5 : 2,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {loading && <Typography>Guardando cambios...</Typography>}
          {error && (
            <Alert severity="error" sx={{ mb: isShortDesktop ? 1 : 2 }}>
              {error}
            </Alert>
          )}
          {errorZonas && (
            <Alert severity="error" sx={{ mb: isShortDesktop ? 1 : 2 }}>
              {errorZonas}
            </Alert>
          )}
          {overlapError && (
            <Alert severity="error" sx={{ mb: isShortDesktop ? 1 : 2 }}>
              {overlapError}
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: isShortDesktop ? 1 : 2,
              "& .MuiTabs-indicator": {
                backgroundColor: "primary.main",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "primary.main",
              },
              ...(isShortDesktop && {
                minHeight: 40,
                "& .MuiTab-root": { minHeight: 40, py: 0.5 },
              }),
              ...(isMobile && {
                mb: 1,
                minHeight: 38,
                "& .MuiTab-root": {
                  minHeight: 38,
                  py: 0.5,
                  px: 0.5,
                  minWidth: 0,
                  fontSize: "0.82rem",
                },
              }),
            }}
            variant={isMobile ? "fullWidth" : "scrollable"}
            scrollButtons={isMobile ? false : "auto"}
            allowScrollButtonsMobile
          >
            <Tab label={isMobile ? "Info" : "Información"} />
            <Tab label="Contacto" />
            <Tab label={huespedesTabLabel} />
          </Tabs>

          <EditReservationForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            activeTab={activeTab}
            zonas={zonas}
            selectedReservation={selectedReservation}
            handleDateChange={handleDateChange}
            handleTextChange={handleTextChange}
            handleMarcarPagado={handleMarcarPagado}
            esPagado={esPagado}
            saldoPendiente={saldoPendiente}
            isShortDesktop={isShortDesktop}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: isMobile ? 1.5 : isShortDesktop ? 1.5 : 2,
            py: isMobile ? 1 : isShortDesktop ? 1.5 : 2,
            pt: isMobile || isShortDesktop ? 1 : 2,
            flexShrink: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading || updateReservationMutation.isPending || !!errors.overlap}
          >
            {loading ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </AppDialog>

      {/* Modal de confirmación para cambio de departamento */}
      <DepartmentChangeModal
        open={showPriceConfirmModal}
        onClose={handleCancelarCambioDepartamento}
        pendingDepartmentChange={pendingDepartmentChange}
        formData={formData}
        onMantenerPrecioEspecial={handleMantenerPrecioEspecial}
        onUsarPrecioBase={handleUsarPrecioBase}
        onCancelar={handleCancelarCambioDepartamento}
      />
    </>
  );
};

export default EditReservationModalRefactored;
